import { metricAliasesById } from "constants/metrics"
import {
  Book,
  Briefcase,
  CaretRight,
  ChartLine,
  Compass,
  Diamond,
  House,
  PenNib,
  Ranking,
  Table,
  User,
} from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { DASHBOARD_ICON } from "modules/studio/components/DashboardImage"
import {
  type Command,
  type Project,
  type Product,
  type MarketSector,
  type Metric,
  type Dashboard,
} from "./types"

const datasetCommands: { title: string; slug: string }[] = [
  { title: "Blockchain comparison", slug: "blockchain-comparison" },
  { title: "Cohort analysis", slug: "cohort-analysis" },
  { title: "Crypto screener", slug: "crypto-screener" },
  { title: "Fundraises", slug: "fundraises" },
  { title: "Insider transactions", slug: "insider-transactions" },
  { title: "Project contracts", slug: "project-contracts" },
  { title: "Stablecoins", slug: "stablecoins" },
  { title: "Top tokenholders", slug: "top-tokenholders" },
  { title: "Trending contracts", slug: "trending-contracts" },
  { title: "Trending wallets", slug: "trending-wallets" },
]

const makeDatasetCommands = () => {
  return datasetCommands.map((dataset) => {
    const command: Command = {
      id: `datasets-${dataset.slug}`,
      key: `datasets-${dataset.slug}`,
      title: `Datasets ${dataset.title}`,
      type: "dataset" as const,
      data: {
        name: dataset.title,
        slug: dataset.slug,
        icon: Table,
      },
    }
    return command
  })
}

export const initialCommands: Command[] = [
  {
    id: "home",
    key: "home",
    title: "Home",
    type: "link",
    data: { href: "/", icon: House },
  },
  {
    id: "discover",
    key: "discover",
    title: "Discover",
    type: "link",
    data: { href: "/discover", icon: Compass },
  },
  {
    id: "market-sectors",
    key: "market-sectors",
    title: "Market sectors",
    type: "link",
    data: { href: "/markets", icon: Briefcase },
  },
  {
    id: "projects",
    key: "projects",
    title: "Projects",
    type: "link",
    data: { href: "/projects", icon: Diamond },
  },
  {
    id: "metrics",
    key: "metrics",
    title: "Metrics",
    type: "link",
    data: {
      href: "/metrics",
      icon: ChartLine,
      labelBadge: {
        size: "xsmall",
        color: "cyan",
        variant: "muted",
        className: "leading-normal",
        children: "New",
      },
    },
  },
  {
    id: "datasets",
    key: "datasets",
    title: "Datasets",
    type: "link",
    data: { href: "/datasets", icon: Table },
  },
  {
    id: "studio",
    key: "studio",
    title: "Studio",
    type: "link",
    data: { href: "/studio/dashboards", icon: PenNib },
  },
  {
    id: "api-reference",
    key: "api-reference",
    title: "API reference",
    type: "link",
    data: {
      href: "https://docs.tokenterminal.com/reference/api-reference",
      icon: Book,
      external: true,
    },
  },
  {
    id: "docs",
    key: "docs",
    title: "Docs",
    type: "link",
    data: {
      href: "https://docs.tokenterminal.com/docs/getting-started",
      icon: Book,
      external: true,
    },
  },
]

export const additionalCommands: Command[] = [
  {
    id: "leaderboards",
    key: "leaderboards",
    title: "Leaderboards",
    type: "link",
    data: { href: "/leaderboards", icon: Ranking },
  },
  {
    id: "create-chart",
    key: "create-chart",
    title: "Create chart",
    type: "link",
    data: { href: "/studio/charts/new", icon: ChartLine },
  },
  {
    id: "create-dashboard",
    key: "create-dashboard",
    title: "Create dashboard",
    type: "link",
    data: { href: "/studio/dashboards/new", icon: DASHBOARD_ICON },
  },
  {
    id: "manage-account",
    key: "manage-account",
    title: "Manage account",
    type: "link",
    data: { href: "/account", icon: User },
  },
  ...makeDatasetCommands(),
  {
    id: "studio-dashboards",
    key: "studio-dashboards",
    title: "Studio Dashboards",
    type: "link",
    renderTitle: () => (
      <div className="flex items-center gap-2">
        <Icon as={PenNib} className="text-fg-secondary" />
        <span className="text-sm text-fg-secondary">Studio</span>
        <Icon as={CaretRight} className="text-fg-secondary" />
        <span className="text-sm">Dashboards</span>
      </div>
    ),
    data: { href: "/studio/dashboards" },
  },
  {
    id: "studio-charts",
    key: "studio-charts",
    title: "Studio Charts",
    type: "link",
    renderTitle: () => (
      <div className="flex items-center gap-2">
        <Icon as={PenNib} className="text-fg-secondary" />
        <span className="text-sm text-fg-secondary">Studio</span>
        <Icon as={CaretRight} className="text-fg-secondary" />
        <span className="text-sm">Charts</span>
      </div>
    ),
    data: { href: "/studio/charts" },
  },
  {
    id: "studio-datasets",
    key: "studio-datasets",
    title: "Studio Datasets",
    type: "link",
    renderTitle: () => (
      <div className="flex items-center gap-2">
        <Icon as={PenNib} className="text-fg-secondary" />
        <span className="text-sm text-fg-secondary">Studio</span>
        <Icon as={CaretRight} className="text-fg-secondary" />
        <span className="text-sm">Datasets</span>
      </div>
    ),
    data: { href: "/studio/datasets" },
  },
]

export const makeProjectCommands = (projects: Project[]): Command[] =>
  projects.map((project) => ({
    id: `project-${project.id}`,
    key: `project-${project.id}`,
    title: project.name,
    keywords: [project.symbol, project.id, project.slug],
    type: "project" as const,
    data: {
      name: project.name,
      logo: project.logo,
      symbol: project.symbol,
      slug: project.slug,
    },
  }))

export const makeProductCommands = (
  products: Product[],
  projectsBySlug: Map<string, Project>
): Command[] =>
  products.map((product) => ({
    id: `product-${product.product_id}`,
    key: `product-${product.product_id}`,
    title: product.product_name,
    type: "product" as const,
    data: {
      product: {
        product_name: product.product_name,
        logo: product.logo,
        symbol: product.symbol,
        project_slug: product.project_slug,
        product_id: product.product_id,
      },
      project: projectsBySlug.get(product.project_slug),
    },
  }))

const marketSectorAliasesById: Record<string, string[]> = {
  stablecoins: ["stablecoin", "stablecoins"],
}

export const makeMarketSectorCommands = (
  marketSectors: MarketSector[]
): Command[] =>
  marketSectors.map((marketSector) => {
    const aliases = marketSectorAliasesById[marketSector.id] || []

    return {
      id: `market-sector-${marketSector.id}`,
      key: `market-sector-${marketSector.id}`,
      title: marketSector.name,
      keywords: [marketSector.id, ...aliases],
      type: "marketSector" as const,
      data: {
        name: marketSector.name,
        slug: marketSector.slug,
      },
    }
  })

export const makeMetricCommands = (
  metrics: Metric[],
  ecosystemMetricIds: string[]
): Command[] => {
  return metrics.map((metric) => {
    const aliases = metricAliasesById[metric.metric_id] || []
    const isEcosystemMetric = ecosystemMetricIds.includes(metric.metric_id)

    return {
      id: `metric-${metric.metric_id}`,
      key: `metric-${metric.metric_id}`,
      title: metric.title,
      keywords: [metric.metric_id, ...aliases],
      type: "metric" as const,
      data: {
        title: metric.title,
        slug: metric.slug,
        isEcosystemMetric,
      },
    }
  })
}

export const makeDashboardCommands = (dashboards: Dashboard[]): Command[] =>
  dashboards.map((dashboard) => ({
    id: `dashboard-${dashboard.user_dashboard_id}`,
    key: `dashboard-${dashboard.user_dashboard_id}`,
    title: dashboard.name ?? "Unnamed dashboard",
    type: "dashboard" as const,
    data: {
      name: dashboard.name ?? "Unnamed dashboard",
      logo_url: dashboard.logo_url,
      user_dashboard_id: dashboard.user_dashboard_id,
    },
  }))

/**
 * Allows us to find project-metric pages using more natural language
 */

export const makeProjectMetricCommands = (
  projects: Project[],
  products: Product[],
  projectsBySlug: Map<string, Project>,
  metricsByMetricId: Map<string, Metric>,
  ecosystemMetricIds: string[]
): Command[] => {
  const results: Command[] = []

  projects.forEach((project) =>
    project.available_metrics?.forEach((metricId) => {
      const metric = metricsByMetricId.get(metricId)
      if (!metric) {
        return
      }

      const isEcosystemMetric = ecosystemMetricIds.includes(metricId)

      const aliases = metricAliasesById[metricId] || []
      results.push({
        id: `project-metric-${project.id}-${metricId}`,
        key: `project-metric-${project.id}-${metricId}`,
        title: `${project.name} ${metric.title}`,
        keywords: [metric.metric_id, ...aliases],
        type: "project-metric" as const,
        data: {
          project: {
            name: project.name,
            logo: project.logo,
            symbol: project.symbol,
            slug: project.slug,
          },
          metric: {
            title: metric.title,
            slug: metric.slug,
            isEcosystemMetric,
          },
        },
      })
    })
  )

  // Placeholder for product-metric results
  // products.forEach((product) => {
  //   product.available_metrics?.forEach((metricId) => {
  //     const metric = metricsByMetricId.get(metricId)
  //     if (!metric) {
  //       return
  //     }

  //     results.push({
  //       id: `product-metric-${product.product_id}-${metricId}`,
  //       key: `product-metric-${product.product_id}-${metricId}`,
  //       title: `${product.product_name} ${metric.title}`,
  //       type: "product-metric" as const,
  //       data: {
  //         product,
  //         metric,
  //       },
  //     })
  //   })
  // })

  return results
}
